import React, {
    useCallback, useContext, useState, useEffect
} from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useHistory } from 'react-router-dom'
import { FaFingerprint, FaUser } from 'react-icons/fa'
import { RiSpam2Line } from 'react-icons/ri'
import { Link } from 'react-router-dom'
import { FiWifi } from 'react-icons/fi'
import { toast } from 'react-toastify'
import ReactTooltip from 'react-tooltip'
import {
    startAuthentication,
    browserSupportsWebAuthn
} from '@simplewebauthn/browser'
import { api } from '../../services/api'
import { criptografar } from '../../util/crypto'
import scroll from '../../util/scroll'
import { GlobalContext } from '../../contexts/GlobalContext'
import { Textbox, Button, List } from '../../components/Form'
import { Container, Content, LoginsContainer } from './styles'
import useQuery from '../../hooks/useQuery'
import PasswordTextbox from '../../components/Form/PasswordTextbox'
import { useGeolocation } from '../../hooks/useGeolocation'

const validation = Yup.object({
    login: Yup.string().required('Login obrigatório.'),
    senha: Yup.string().required('Senha obrigatória.')
})

const baseInitialValues = {
    login: '',
    senha: ''
}

export default function SignIn() {
    scroll.toTop()

    const { setUser } = useContext(GlobalContext)

    const history = useHistory()

    const planoContratacaoString = localStorage.getItem('sigas:carrinho')
    const plano_contratacao = planoContratacaoString ? JSON.parse(planoContratacaoString) : null

    const { registerPageAccess } = useGeolocation()

    const [login, setLogin] = useState({
        login: '',
        senha: ''
    })
    const [crm, setCrm] = useState('')
    const [initialValues, setInitialValues] = useState(baseInitialValues)
    const [showConfirmacaoBox, setShowConfirmacaoBox] = useState(false)
    const [storedUsername, setStoredUsername] = useState(null)
    const [estaAutenticandoPorBiometria, setEstaAutenticandoPorBiometria] = useState(false)
    const [biometriaDisponivel, setBiometriaDisponivel] = useState(false)

    const { enquete } = useQuery()

    function handlePostSignin(session, formikBag) {
        try {
            setUser(session.usuario)

            const ciphed = criptografar(JSON.stringify(session.usuario))

            sessionStorage.setItem(process.env.REACT_APP_SESSION_STORAGE_TOKEN, session.token)
            sessionStorage.setItem(process.env.REACT_APP_SESSION_STORAGE_USER, ciphed)

            formikBag?.setSubmitting(false)

            const deveEsvaziarCarrinho = plano_contratacao ?
                session.usuario.pessoa?.assinaturas?.some(a => a?.status === 'authorized' && a?.plano.modulo_id === plano_contratacao?.modulo.id) :
                false

            if (deveEsvaziarCarrinho) {
                localStorage.removeItem('sigas:carrinho')
            }

            if (enquete) {
                history.push(`/enquete/${enquete}/votar`)
            } else {
                if (session.usuario.pessoa.tipo === 'PF') {
                    const isAdmin = session.usuario.perfil.id === 1
                    const isAdminEmpresa = session.usuario.perfil.id === 3

                    if (plano_contratacao) {
                        const { assinaturas } = session.usuario.pessoa
                        const jaContratado = assinaturas?.some(assinatura => assinatura.status === 'authorized' && assinatura.plano.modulo_id === plano_contratacao.modulo.id) || false

                        if (plano_contratacao.id === 1 && !jaContratado) { // Plano gratuito
                            history.push(`/contratacao/gratis/${plano_contratacao.id}`)
                        } else if (jaContratado) {
                            history.push('/dashboard')
                        } else {
                            history.push('/auth/checkout')
                        }
                    } else if (isAdmin || isAdminEmpresa) {
                        history.push('/admin')
                    } else {
                        history.push('/dashboard')
                    }
                } else {
                    history.push('/dashboard_empresa')
                }
            }
        } catch (e) {
            if (e.confirmacao) {
                setShowConfirmacaoBox(true)
            } else {
                toast.error(e.msg || e)
            }
        }
    }

    const handleSubmit = useCallback(async (values, formikBag) => {
        try {
            const session = await api.post('autenticar', values)

            if (session.requires_google_auth) {
                toast.warn(session.msg)
                return
            }

            if (session.confirmacao) {
                localStorage.setItem('sigas-confirmation-account-temp-data', values.login)

                history.push({
                    pathname: '/confirmacao_conta',
                    state: {
                        usuario_criado: true
                    }
                })

                return
            }

            if (session.usuario?.biometria) {
                localStorage.setItem('sigas:username', session.usuario.biometria.email)
            }

            handlePostSignin(session, formikBag)
        } catch (e) {
            toast.error(e.msg || e)
        }
    }, [])

    async function handleReenviarLinkConfirmacao(email) {
        try {
            await api.post('usuario/reenviar_confirmacao', { email })

            localStorage.setItem('sigas-confirmation-account-temp-data', email)

            toast.success('Acabamos de reenviar o link de confirmação. Acesse sua caixa de entrada.')

            history.push({
                pathname: '/confirmacao_conta',
                state: {
                    usuario_criado: true
                }
            })
        } catch (e) {
            toast.error(e.msg)
        }
    }

    async function handleSigninBiometria() {
        try {
            if (!storedUsername) {
                return
            }

            setEstaAutenticandoPorBiometria(true)

            const options = await api.get('init-auth', {
                withCredentials: true,
                params: {
                    email: storedUsername
                }
            })

            const authJSON = await startAuthentication(options)

            const verifyData = await api.post('verify-auth', authJSON, {
                withCredentials: true
            })

            if (verifyData.verified) {
                const session = await api.post('autenticar_com_biometria', {
                    email: storedUsername
                })

                handlePostSignin(session)
            } else {
                toast.error('Erro na verificação')
            }
        } catch (e) {
            console.log('erro', e)
        } finally {
            setEstaAutenticandoPorBiometria(false)
        }
    }

    async function handleSigninGoogle(data) {
        try {
            const jwt = data.credential

            const session = await api.post('login_google', { token: jwt })

            handlePostSignin(session)
        } catch (e) {
            toast.error('Erro ao autenticar com Google.')
        }
    }

    useEffect(() => {
        setInitialValues(login)
        setCrm(login.login)
    }, [login])

    useEffect(() => {
        const storedUsernameString = localStorage.getItem('sigas:username')

        setStoredUsername(storedUsernameString)

        checkBiometria()
    }, [])

    useEffect(() => {
        if (plano_contratacao) {
            registerPageAccess({
                plano: plano_contratacao.id
            })
        }
    }, [plano_contratacao])

    async function checkBiometria() {
        setBiometriaDisponivel(browserSupportsWebAuthn())
    }

    return (
        <>
            <Container>
                <div className="space" style={{ width: '100%' }} />

                <div className="flex flex-col gap-4">
                    <Content className="animated fadeIn delay-200ms bg-white text-slate-800">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validation}
                            onSubmit={handleSubmit}
                            enableReinitialize
                        >
                            {({ isSubmitting, values }) => (
                                <Form>
                                    <h1 className="text-slate-800">Login</h1>

                                    <Textbox
                                        name="login"
                                        label="CPF ou e-mail"
                                        onChange={e => {
                                            setCrm(e.target.value)
                                            setLogin({
                                                login: e.target.value,
                                                senha: login.senha
                                            })
                                        }}
                                        value={crm}
                                        icon={{ source: FaUser, size: 18 }}
                                    />

                                    <PasswordTextbox name="senha" label="Senha" disableSubmitWithEnter={false} />

                                    {showConfirmacaoBox && (
                                        <>
                                            <div className="bg-yellow-500 p-2 rounded-md text-slate-800 mt-3 mx-2 animated flash">
                                                {`Acesse o e-mail ${crm} e confirme seu cadastro.`}
                                            </div>

                                            <span className="mt-3 text-xs flex items-center gap-1 self-center text-slate-400">
                                                <RiSpam2Line size={17} />
                                                Verifique a caixa de SPAM.
                                            </span>

                                            <span
                                                className="cursor-pointer py-2 px-3 text-sky-600 hover:opacity-70 mt-2"
                                                onClick={() => { handleReenviarLinkConfirmacao(values.login) }}
                                            >
                                                Não recebeu o link?<br />Clique para reenviar.
                                            </span>
                                        </>
                                    )}

                                    <Button type="submit" loading={isSubmitting}>Entrar</Button>

                                    {/* <div className="mx-2 mt-5 flex flex-col items-center">
                                        <GoogleLogin
                                            onSuccess={handleSigninGoogle}
                                            onError={() => {
                                                toast.error('Falha no login com Google.')
                                            }}
                                            context="signin"
                                            size="medium"
                                            useOneTap
                                        />
                                    </div> 
                                    */}

                                    <Link to="/pass_recover" className="mt-6 text-sky-700">Esqueci a senha</Link>

                                    <div
                                        className="text-sky-700 mt-4 cursor-pointer hover:opacity-70 hover:underline"
                                        onClick={() => {
                                            history.push({
                                                pathname: '/signup',
                                                state: {
                                                    plano_contratacao
                                                }
                                            })
                                        }}
                                    >
                                        Criar minha conta
                                    </div>
                                </Form>
                            )}
                        </Formik>

                        {/* {biometriaDisponivel && storedUsername && (
                            <div className="mt-3 flex flex-col gap-3" style={{ width: 'calc(100% - 16px)' }}>
                                <Button 
                                    onClick={handleSigninBiometria} 
                                    className="w-full bg-sky-500 gap-2 flex-col" 
                                    style={{ padding: 12, height: 60 }}
                                    loading={estaAutenticandoPorBiometria}
                                >
                                    <div className="flex gap-2 items-center">
                                        <FaFingerprint size={15} />
                                        Entrar com PIN / biometria
                                    </div>

                                    <span className="text-xs">{storedUsername}</span>
                                </Button>

                                <span 
                                    className="text-xs cursor-pointer text-sky-700 hover:opacity-70 hover:underline"
                                    onClick={() => {
                                        localStorage.removeItem('sigas:username')

                                        setStoredUsername(null)
                                    }}
                                >
                                    Não é você?
                                </span>
                            </div>
                        )} */}
                    </Content>

                    <p className="mx-4 text-slate-300 flex gap-2 text-xs w-72 mb-4 animated fadeIn delay-1s">
                        <FiWifi size={23} />
                        <span>Este aplicativo precisa de conexão com a internet para funcionar.</span>
                    </p>
                </div>

                {process.env.REACT_APP_ENVIRONMENT ? process.env.REACT_APP_ENVIRONMENT !== 'production' && (
                    <LoginsPredefinidos setLogin={setLogin} />
                ) : <></>}
            </Container>

            <ReactTooltip
                place="top"
                effect="solid"
                type="dark"
            />
        </>
    )
}

function LoginsPredefinidos({ setLogin }) {
    return (
        <LoginsContainer className="animated fadeInRight faster delay-500ms bg-white text-slate-800 h-fit">
            <h1 className="text-slate-800">Logins predefinidos</h1>

            <p>{`Ambiente: ${process.env.REACT_APP_ENVIRONMENT}`}</p>

            <List
                options={[
                    { label: 'Joel Cunha', value: 'pessoa' },
                    { label: 'Juliana Leite', value: 'pessoa2' },
                    { label: 'Admin', value: 'admin' }
                ]}
                onChange={e => {
                    switch (e.value) {
                        case 'pessoa':
                            setLogin({
                                login: 'lipi4shared@hotmail.com',
                                senha: '123123'
                            })
                            break

                        case 'pessoa2':
                            setLogin({
                                login: 'juliana@gmail.com',
                                senha: 'Abc123!@#'
                            })
                            break

                        case 'admin':
                            setLogin({
                                login: 'felipe@email.com',
                                senha: '123123'
                            })
                            break

                        default: break
                    }
                }}
            />
        </LoginsContainer>
    )
}